@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.engineservice.support

import androidx.compose.runtime.*
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.ScreenManager
import org.example.project.service.ZustandManager
import org.example.project.settings.AppDropdown
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private fun supportScreenLabel(screenKey: String): String =
    ScreenManager.labelForKey(screenKey).ifBlank { screenKey }

@Composable
internal fun CUSSupportLayout(
    showBackButton: Boolean,
    hint: String?,
    onBack: () -> Unit,
    content: @Composable () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            width(100.percent)
            height(100.percent)
            color(Color.white)
            property("font-family", "Inter, system-ui, sans-serif")
        }
    }) {
        Div({
            style {
                width(100.percent)
                height(100.percent)
                backgroundColor(Color.transparent)
                property("border", "none")
                borderRadius(0.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Row)
                overflow("visible")
                property("box-sizing", "border-box")
                padding(18.px)
            }
        }) {
            Div({
                style {
                    width(72.px)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    opacity(if (showBackButton) 0.6 else 0.0)
                    property("pointer-events", if (showBackButton) "auto" else "none")
                    cursor("pointer")
                }
                onClick { onBack() }
            }) {
                Img(src = "images/back.svg") {
                    style {
                        width(28.px)
                        height(28.px)
                        property("filter", "brightness(0) invert(1)")
                    }
                }
            }

            Div({
                style {
                    flexGrow(1)
                    padding(36.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.FlexStart)
                    property("box-sizing", "border-box")
                    position(Position.Relative)
                }
            }) {
                hint?.let {
                    P({ style { margin(0.px); marginBottom(12.px); color(Color("#9aa4b5")) } }) { Text(it) }
                }

                Div({
                    style {
                        width(100.percent)
                        flexGrow(1)
                        minHeight(0.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        justifyContent(JustifyContent.FlexStart)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    content()
                }
            }

            Div({ style { width(72.px); opacity(0.0); property("pointer-events", "none") } }) {}
        }
    }
}

@Composable
internal fun CUSSupportHub(
    onCreateTicket: () -> Unit,
    onShowHistory: () -> Unit
) {
    Div({
        style {
            flexGrow(1)
            width(100.percent)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            gap(28.px)
        }
    }) {
        SupportHubCard("Ticket erstellen", "images/add_circle.svg", onCreateTicket)
        SupportHubCard("Ticket-Verlauf", "images/tickets.svg", onShowHistory)
    }
}

@Composable
internal fun SupportHubCard(
    title: String,
    icon: String,
    onClick: () -> Unit
) {
    var hover by remember { mutableStateOf(false) }
    Div({
        style {
            width(185.px)
            padding(30.px, 18.px)
            borderRadius(16.px)
            backgroundColor(DashboardTheme.SURFACE_CARD)
            property("border", if (hover) "1px solid ${UISettings.ACCENT}" else "1px solid rgba(255,255,255,0.34)")
            property("transition", "border-color 0.18s ease")
            cursor("pointer")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(18.px)
        }
        onMouseEnter { hover = true }
        onMouseLeave { hover = false }
        onClick { onClick() }
    }) {
        Div({
            style {
                width(54.px)
                height(54.px)
                borderRadius(8.px)
                backgroundColor(Color.transparent)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("box-sizing", "border-box")
            }
        }) {
            Img(src = icon, attrs = {
                style {
                    width(42.px)
                    height(42.px)
                    property("object-fit", "contain")
                    property(
                        "filter",
                        if (hover) UISettings.ICON_FILTER_ACCENT else "brightness(0) invert(1)"
                    )
                }
            })
        }
        Span({ style { fontWeight("600"); fontSize(15.px); textAlign("center") } }) { Text(title) }
    }
}

@Composable
private fun SupportBrandCard(
    icon: String,
    onClick: () -> Unit
) {
    var hover by remember { mutableStateOf(false) }
    val isChatGptLogo = icon.contains("brand_chatgpt")
    Div({
        style {
            width(132.px)
            height(132.px)
            padding(0.px)
            borderRadius(8.px)
            backgroundColor(DashboardTheme.SURFACE_CARD)
            property("border", if (hover) "1px solid ${UISettings.ACCENT}" else "1px solid rgba(255,255,255,0.34)")
            property("transition", "border-color 0.18s ease")
            cursor("pointer")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("box-sizing", "border-box")
        }
        onMouseEnter { hover = true }
        onMouseLeave { hover = false }
        onClick { onClick() }
    }) {
        Img(src = icon, attrs = {
            style {
                width(74.px)
                height(74.px)
                property("object-fit", "contain")
                if (isChatGptLogo) {
                    property("filter", "brightness(0) invert(1)")
                }
            }
        })
    }
}

@Composable
internal fun CUSSupportTicketForm(
    screenOptions: List<String>,
    modul: String,
    problem: String,
    loading: Boolean,
    onModulChange: (String) -> Unit,
    onProblemChange: (String) -> Unit,
    onSubmit: () -> Unit
) {
    Div({
        style {
            width(100.percent)
            maxWidth(980.px)
            flexGrow(1)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(18.px)
            property("margin", "0 auto")
            padding(16.px, 0.px, 32.px, 0.px)
            property("box-sizing", "border-box")
        }
    }) {
        AppDropdown(
            options = buildList {
                add(All(key = "", label = "Fehlerort auswählen"))
                addAll(screenOptions.map { All(key = it, label = supportScreenLabel(it)) })
            },
            selectedValue = modul,
            onChange = onModulChange,
            attrs = {
                style {
                    width(100.percent)
                    height(54.px)
                    padding(0.px, 18.px)
                    borderRadius(10.px)
                    backgroundColor(Color("#000000"))
                    color(Color.white)
                    property("border", "1px solid #2a2d34")
                    property("box-sizing", "border-box")
                    fontSize(15.px)
                }
            }
        )

        TextArea(attrs = {
            style {
                width(100.percent)
                minHeight(360.px)
                padding(18.px)
                borderRadius(10.px)
                backgroundColor(Color("#000000"))
                color(Color.white)
                property("border", "1px solid #2a2d34")
                property("box-sizing", "border-box")
                property("resize", "vertical")
                property("caret-color", "white")
                property("font-family", "inherit")
                fontSize(15.px)
                lineHeight("1.55")
            }
            value(problem)
            onInput { onProblemChange(it.value) }
            placeholder("Problembeschreibung")
        })

        Button(attrs = {
            style {
                marginTop(12.px)
                width(100.percent)
                height(50.px)
                borderRadius(8.px)
                backgroundColor(if (modul.isNotBlank() && problem.isNotBlank()) Color.white else Color("#1a1c20"))
                color(if (modul.isNotBlank() && problem.isNotBlank()) Color.black else Color("#4a4d54"))
                fontWeight("bold")
                property("border", "none")
                cursor("pointer")
            }
            if (loading || modul.isBlank() || problem.isBlank()) disabled()
            onClick { onSubmit() }
        }) { Text(if (loading) "..." else "Weiter zu KI") }
    }
}

@Composable
internal fun CUSSupportKiChoice(
    onChatGpt: () -> Unit,
    onGemini: () -> Unit,
    onCopilot: () -> Unit
) {
    Div({
        style {
            position(Position.Absolute)
            left(50.percent)
            top(50.percent)
            property("transform", "translate(-50%, -50%)")
            textAlign("center")
        }
    }) {
        Div({
            style {
                position(Position.Absolute)
                left(50.percent)
                top((-120).px)
                property("transform", "translateX(-50%)")
                width(100.percent)
                maxWidth(760.px)
            }
        }) {
            P({
                style {
                    margin(0.px)
                    color(Color("#9aa4b5"))
                    lineHeight("1.45")
                }
            }) {
                Text("Bitte wähle die KI deiner Wahl, lade das Dokument dort hoch und befolge die nächsten Schritte.")
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Row)
                gap(28.px)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                property("flex-wrap", "nowrap")
            }
        }) {
            SupportBrandCard("images/brand_chatgpt.svg", onChatGpt)
            SupportBrandCard("images/brand_gemini.svg", onGemini)
            SupportBrandCard("images/brand_copilot.svg", onCopilot)
        }
    }
}

@Composable
internal fun CUSSupportAbschluss(
    abschlussArt: String,
    problem: String,
    loesungsweg: String,
    loading: Boolean,
    onAbschlussArtChange: (String) -> Unit,
    onLoesungswegChange: (String) -> Unit,
    onSubmit: () -> Unit,
    showOutcomeChoice: Boolean = true,
    solvedLabel: String = "KI hat Fall gelöst",
    unsolvedLabel: String = "KI konnte nicht helfen",
    inputPlaceholder: String = "Abschlussanalyse der KI hier einfügen.",
    submitLabel: String = "Ticket absenden"
) {
    Div({
        style {
            width(100.percent)
            maxWidth(760.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(16.px)
            padding(18.px, 20.px)
            property("box-sizing", "border-box")
        }
    }) {
        if (showOutcomeChoice) Div({
            style {
                width(100.percent)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                gap(14.px)
                marginBottom(8.px)
            }
        }) {
            Button(attrs = {
                style {
                    width(280.px)
                    height(44.px)
                    padding(0.px, 18.px)
                    borderRadius(8.px)
                    backgroundColor(Color.transparent)
                    color(Color.white)
                    property("border", if (abschlussArt == "GELOEST") "1px solid rgba(255,255,255,0.75)" else "1px solid #2a2d34")
                    cursor("pointer")
                }
                onClick { onAbschlussArtChange("GELOEST") }
            }) { Text(solvedLabel) }

            Button(attrs = {
                style {
                    width(280.px)
                    height(44.px)
                    padding(0.px, 18.px)
                    borderRadius(8.px)
                    backgroundColor(Color.transparent)
                    color(Color.white)
                    property("border", if (abschlussArt == "OFFEN") "1px solid rgba(255,255,255,0.75)" else "1px solid #2a2d34")
                    cursor("pointer")
                }
                onClick { onAbschlussArtChange("OFFEN") }
            }) { Text(unsolvedLabel) }
        }

        if (abschlussArt == "GELOEST") {
            TextArea(attrs = {
                style {
                    width(100.percent)
                    minHeight(110.px)
                    padding(16.px)
                    marginBottom(10.px)
                    borderRadius(8.px)
                    backgroundColor(Color.transparent)
                    color(Color.white)
                    property("border", "1px solid #2a2d34")
                    property("box-sizing", "border-box")
                    property("resize", "none")
                    property("caret-color", "white")
                }
                value(loesungsweg)
                onInput { onLoesungswegChange(it.value) }
                placeholder(inputPlaceholder)
            })
        } else {
            Div(attrs = {
                style {
                    width(100.percent)
                    padding(16.px)
                    marginBottom(10.px)
                    borderRadius(8.px)
                    backgroundColor(Color.transparent)
                    color(Color.white)
                    property("border", "1px solid #2a2d34")
                    property("box-sizing", "border-box")
                    property("white-space", "pre-wrap")
                }
            }) {
                Text("Problembeschreibung:")
                Br()
                Br()
                Text(problem.ifBlank { "-" })
            }

            TextArea(attrs = {
                style {
                    width(100.percent)
                    minHeight(230.px)
                    padding(16.px)
                    marginBottom(10.px)
                    borderRadius(8.px)
                    backgroundColor(Color.transparent)
                    color(Color.white)
                    property("border", "1px solid #2a2d34")
                    property("box-sizing", "border-box")
                    property("resize", "none")
                    property("caret-color", "white")
                }
                value(loesungsweg)
                onInput { onLoesungswegChange(it.value) }
                placeholder(inputPlaceholder)
            })
        }

        Button(attrs = {
            style {
                marginTop(14.px)
                width(340.px)
                height(46.px)
                borderRadius(8.px)
                backgroundColor(Color.transparent)
                color(Color.white)
                fontWeight("bold")
                property("border", "1px solid rgba(255,255,255,0.3)")
                cursor("pointer")
                alignSelf("center")
            }
            if (loading) disabled()
            onClick { onSubmit() }
        }) { Text(if (loading) "..." else submitLabel) }
    }
}

@Composable
internal fun CUSSupportDoneSolved(onOpenPdf: (() -> Unit)? = null) {
    Div({
        style {
            flexGrow(1)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            textAlign("center")
            width(100.percent)
            cursor(if (onOpenPdf != null) "pointer" else "default")
        }
        onOpenPdf?.let { handler -> onClick { handler() } }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                gap(8.px)
            }
        }) {
            Span({ style { fontSize(18.px); fontWeight("600") } }) {
                Text("Danke das du den lumatica KI Support genutzt hast.")
            }
            Span({ style { color(Color("#9aa4b5")) } }) {
                Text("Dein lumatica Support Team")
            }
        }
    }
}

@Composable
internal fun CUSSupportDoneOpen(onOpenPdf: (() -> Unit)? = null) {
    Div({
        style {
            flexGrow(1)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            textAlign("center")
            width(100.percent)
            cursor(if (onOpenPdf != null) "pointer" else "default")
        }
        onOpenPdf?.let { handler -> onClick { handler() } }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                gap(8.px)
                maxWidth(860.px)
            }
        }) {
            Span({ style { whiteSpace("pre-line"); fontSize(18.px); fontWeight("600") } }) {
                Text("Es tut uns Leid das der KI Support dir nicht helfen konnte,\ndein Anliegen wird daher an den Second Level Support weitergeleitet.\nWir melden uns unmittelbar bei dir!\nDein lumatica Support Team")
            }
        }
    }
}

@Composable
internal fun CUSSupportVerlaufLayout(
    verlauf: List<All>,
    verlaufFilter: String,
    onFilterChange: (String) -> Unit,
    onOpenClosedTicket: (All) -> Unit
) {
    val offeneTickets = verlauf.count { it.zustand != ZustandManager.Support.ABGESCHLOSSEN }
    val abgeschlosseneTickets = verlauf.count { it.zustand == ZustandManager.Support.ABGESCHLOSSEN }
    val filteredVerlauf = when (verlaufFilter) {
        "OFFEN" -> verlauf.filter { it.zustand != ZustandManager.Support.ABGESCHLOSSEN }
        "CLOSE" -> verlauf.filter { it.zustand == ZustandManager.Support.ABGESCHLOSSEN }
        else -> verlauf
    }

    Div({
        style {
            width(100.percent)
            maxWidth(760.px)
            flexGrow(1)
            minHeight(0.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(18.px)
        }
    }) {
        Div({
            style {
                width(100.percent)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                gap(14.px)
                paddingBottom(16.px)
                property("flex-wrap", "wrap")
            }
        }) {
            @Composable
            fun counterItem(icon: String, value: Int, filterKey: String) {
                val numberColor = when (icon) {
                    "images/tickets_open.svg" -> "#ff4d4f"
                    "images/tickets_close.svg" -> "#66BB6A"
                    else -> "#ffffff"
                }
                val tint = when (icon) {
                    "images/tickets_open.svg" -> "brightness(0) saturate(100%) invert(24%) sepia(92%) saturate(2920%) hue-rotate(344deg) brightness(98%) contrast(93%)"
                    "images/tickets_close.svg" -> "brightness(0) saturate(100%) invert(65%) sepia(31%) saturate(690%) hue-rotate(78deg) brightness(94%) contrast(89%)"
                    else -> "brightness(0) invert(1)"
                }
                Div({
                    style {
                        width(156.px)
                        height(72.px)
                        padding(8.px, 12.px)
                        borderRadius(8.px)
                        backgroundColor(DashboardTheme.SURFACE_CARD)
                        property(
                            "border",
                            if (verlaufFilter == filterKey) "1px solid ${DashboardTheme.ACCENT}" else "1px solid ${DashboardTheme.BORDER_LIGHT}"
                        )
                        display(DisplayStyle.Grid)
                        gridTemplateColumns("54px minmax(0, 1fr)")
                        alignItems(AlignItems.Center)
                        gap(8.px)
                        cursor("pointer")
                        property("box-sizing", "border-box")
                    }
                    onClick { onFilterChange(filterKey) }
                }) {
                    Img(src = icon) {
                        style {
                            width(42.px)
                            height(42.px)
                            property("filter", tint)
                        }
                    }
                    Span({
                        style {
                            fontSize(24.px)
                            fontWeight("750")
                            textAlign("center")
                            color(Color(numberColor))
                            property("color", numberColor)
                        }
                    }) { Text(value.toString()) }
                }
            }

            Div({
                style {
                    width(54.px)
                    height(72.px)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                }
            }) {
                Img(src = "images/filter_list_off.svg") {
                    style {
                        width(28.px)
                        height(28.px)
                        property("filter", "brightness(0) invert(1)")
                    }
                }
            }
            counterItem("images/tickets.svg", verlauf.size, "ALLE")
            counterItem("images/tickets_open.svg", offeneTickets, "OFFEN")
            counterItem("images/tickets_close.svg", abgeschlosseneTickets, "CLOSE")
        }

        Div({
            style {
                width(100.percent)
                maxWidth(900.px)
                flexGrow(1)
                minHeight(0.px)
                alignSelf(AlignSelf.Center)
                padding(8.px)
                property("box-sizing", "border-box")
                property("border", "1px solid transparent")
                borderRadius(10.px)
                overflowY("auto")
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
            }
        }) {
            if (filteredVerlauf.isEmpty()) {
                Text("Keine Tickets vorhanden.")
            } else {
                filteredVerlauf.forEach { ticket ->
                    val statusIcon = if (ticket.zustand == ZustandManager.Support.ABGESCHLOSSEN) {
                        "images/tickets_close.svg"
                    } else {
                        "images/tickets_open.svg"
                    }
                    val statusTint = if (ticket.zustand == ZustandManager.Support.ABGESCHLOSSEN) {
                        "brightness(0) saturate(100%) invert(65%) sepia(31%) saturate(690%) hue-rotate(78deg) brightness(94%) contrast(89%) drop-shadow(0 0 8px rgba(102,187,106,0.65))"
                    } else {
                        "brightness(0) saturate(100%) invert(24%) sepia(92%) saturate(2920%) hue-rotate(344deg) brightness(98%) contrast(93%) drop-shadow(0 0 8px rgba(255,77,79,0.65))"
                    }
                    Div({
                        style {
                            marginBottom(12.px)
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.Center)
                            width(100.percent)
                            property("box-sizing", "border-box")
                        }
                    }) {
                        Div({
                            style {
                                width(420.px)
                                height(100.px)
                                padding(10.px)
                                borderRadius(8.px)
                                backgroundColor(DashboardTheme.SURFACE_CARD)
                                property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                                display(DisplayStyle.Grid)
                                gridTemplateColumns("76px minmax(0, 1fr)")
                                alignItems(AlignItems.Center)
                                gap(10.px)
                                property("box-sizing", "border-box")
                                cursor(if (ticket.zustand == ZustandManager.Support.ABGESCHLOSSEN) "pointer" else "default")
                            }
                            if (ticket.zustand == ZustandManager.Support.ABGESCHLOSSEN) {
                                onClick { onOpenClosedTicket(ticket) }
                            }
                        }) {
                            Div({
                                style {
                                    width(76.px)
                                    height(80.px)
                                    position(Position.Relative)
                                    flexShrink(0)
                                }
                            }) {
                                Img(src = statusIcon) {
                                    style {
                                        width(50.px)
                                        height(50.px)
                                        position(Position.Absolute)
                                        left(50.percent)
                                        top(50.percent)
                                        property("transform", "translate(-50%, -50%)")
                                        property("filter", statusTint)
                                    }
                                }
                            }
                            Div({
                                style {
                                    display(DisplayStyle.Grid)
                                    gridTemplateRows("1fr 1fr")
                                    alignItems(AlignItems.Center)
                                    justifyItems("center")
                                    minWidth(0.px)
                                    textAlign("center")
                                }
                            }) {
                                Span({
                                    style {
                                        width(100.percent)
                                        fontSize(16.px)
                                        fontWeight("700")
                                        property("white-space", "nowrap")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                    }
                                }) {
                                    Text(ticket.ticketnummer.ifBlank { "SUP-${ticket.id}" })
                                }
                                Span({
                                    style {
                                        width(100.percent)
                                        color(DashboardTheme.TEXT_MUTED)
                                        fontSize(13.px)
                                        fontWeight("600")
                                        property("white-space", "nowrap")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                    }
                                }) {
                                    Text(ZustandManager.Support.textFor(ticket.zustand))
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
